import qc from 'qc'
import {
  isAudio,
  isDragonBone,
  isDragonBoneChild,
  isEmptyNode,
  isGroup,
  isImage,
  isParticle,
  isParticleParent,
  isSameNodeType,
  isScene,
  isSequenceFrame,
  isText,
  isVideo,
} from '@ps/bridge/qc/base'
import { getLayoutStyleBySize } from '@ps/store/workbench/common/node-utils'
import { executeAction, getPasteNodeStyleAction } from '@ps/workbench/undo-redo'
import store from '@ps/store'
import { getFont } from '@ps/bridge/qc/node/text'
import nodePool from '@ps/bridge/qc/node-pool'
import { getNodeStyle } from '@ps/bridge/qc/node'
import { getTextByFont } from '@ps/workbench/layer/text'

/**
 * 判断节点是否有样式用于复制粘贴
 * @param node
 */
export function styleCanBeCopiedAndPasted(node: qc.Node): boolean {
  return !isAudio(node) && !isScene(node) && !isDragonBoneChild(node)
}

export function styleCanBePasted(
  targetNodes: qc.Node[],
  originalNode: qc.Node,
): boolean {
  return (
    targetNodes.every((node) => styleCanBeCopiedAndPasted(node)) &&
    styleCanBeCopiedAndPasted(originalNode) &&
    targetNodes.every((node) => isSameNodeType(node, originalNode))
  )
}

function pickNodeAttrs(node: qc.Node): string[] | void {
  const utils = [
    {
      validate: (node: qc.Node) => {
        return (
          isGroup(node) ||
          isImage(node) ||
          isSequenceFrame(node) ||
          isDragonBone(node) ||
          isEmptyNode(node)
        )
      },
      attrs: ['width', 'height', 'alpha', 'rotation', 'scaleX', 'scaleY'],
    },
    {
      validate: isText,
      attrs: [
        'width',
        'height',
        'alpha',
        'rotation',
        'scaleX',
        'scaleY',
        'alignH', // 对齐方式 1
        'alignV', // 对齐方式 2
        'font', // 字体
        'color', // 文字颜色
        'gradient', // 开启渐变
        'startColor', // 渐变颜色
        'endColor', // 渐变颜色
        'fontSize', // 字号
        'letterSpacing', // 字距
        'lineSpacing', // 行高
        'bold', // 粗体
        'italic', // 斜体
        'enableDeleteline', // 删除线
        'enableUnderline', // 下划线
        'stroke', // 描边颜色
        'strokeThickness', // 描边粗细
        'enableShadow', // 开启阴影
        'shadowColor', // 阴影颜色
        'shadowOffsetX', // 阴影x偏移
        'shadowOffsetY', // 阴影y偏移
        'shadowBlur', // 阴影模糊值
      ],
    },
    {
      validate: isVideo,
      attrs: ['width', 'height', 'scaleX', 'scaleY'],
    },
    {
      validate: (node: qc.Node) => {
        return isParticle(node) || isParticleParent(node)
      },
      attrs: ['width', 'height', 'rotation', 'scaleX', 'scaleY'],
    },
  ]
  const cur = utils.find((item) => item.validate(node))
  if (cur && cur.attrs) {
    return cur.attrs
  }
}

export function getStyle(node: qc.Node): Record<string, any> | null {
  if (styleCanBeCopiedAndPasted(node)) {
    return getNodeStyle({
      node: node,
      attrs: pickNodeAttrs(node) as string[],
    })
  }
  return null
}

type PasteStyleParams = {
  targetNodes: qc.Node[]
  record: boolean
  style: Record<string, any>
}

/**
 * 粘贴样式
 * @param targetNodes 目标节点
 * @param record 是否需要历史记录
 * @param style 新样式
 */
export async function pasteStyle({
  targetNodes,
  record = true,
  style,
}: PasteStyleParams) {
  if (!targetNodes) return
  // 如果有字体，需要先加载字体
  if (style.font) {
    const fontList = await store.dispatch('config/getFontList')
    const assetFontList = store.state.assets.assetFontList
    const fontName = style.font
    const textArr =
      targetNodes
        .filter((node) => isText(node))
        .map((node) => (node as qc.UIText).text) || []
    const text = textArr.join('').replace(/\s/g, '') || ''
    const newFontName = await getFont({
      fontName,
      text: getTextByFont(fontName, text),
      fontList: fontList.concat(assetFontList),
      pid: store.state.workspace.pid,
    })

    style.font = newFontName
  }

  const oldStyles: {
    [key: string]: Record<string, any>
  } = {}
  const styles: {
    [key: string]: Record<string, any>
  } = {}
  // 如果有后代节点，宽高的变化需要影响其后代节点
  targetNodes.forEach((node) => {
    const data = getLayoutStyleBySize(node, {
      width: style.width,
      height: style.height,
      scaleX: style.scaleX,
      scaleY: style.scaleY,
    })
    const newStyle = data.style
    const oldStyle = data.oldStyle
    styles[node.uuid] = newStyle.map((item) => ({
      ...style,
      ...item,
    }))
    oldStyles[node.uuid] = oldStyle.map((item) => ({
      ...getStyle(nodePool.find(item.uuid)),
      ...item,
    }))
  })

  executeAction({
    action: getPasteNodeStyleAction({
      newValue: {
        styles,
        oldStyles,
        nodes: targetNodes,
      },
      oldValue: {
        styles: oldStyles,
        nodes: targetNodes,
      },
    }),
    record,
  })
}
